import { Button } from '@cambridgeassessment/cambridge-ui';
import { Typography } from '@material-ui/core';
import React, { memo, useCallback, useMemo } from 'react';
import useAuth from 'src/hooks/useAuth';
import useNotifications from 'src/hooks/useNotifications';
import {
  disableSocialProvider,
  enableSocialProvider,
} from 'src/services/gigya/gigya-service';
import envVars from '../../env';

interface SocialAccountButtonProps {
  title: string;
  isConnected: boolean;
}

const SocialAccountButton: React.FC<SocialAccountButtonProps> = ({
  title,
  isConnected,
}) => {
  const { update } = useAuth();
  const { addNotification } = useNotifications();

  const { IS_CHINA } = useMemo(() => envVars, []);

  const errors = useMemo(
    () => ({
      not_allowed: 'Exceeded the number of allowed changes.',
      main: `Could not ${
        isConnected ? 'disconnect' : 'connect'
      } the account. Please try again.`,
    }),
    [isConnected]
  );

  const connectionCallback = useCallback(
    (info) => {
      if (!info.errorCode) {
        addNotification({
          title: `${title} account ${
            isConnected ? 'disconnected' : 'connected'
          }.`,
          variant: 'success',
          timeout: 5000,
        });
        update();
      } else {
        const errMsg =
          info.errorMessage === 'Operation not allowed'
            ? errors.not_allowed
            : errors.main;
        addNotification({
          title: errMsg,
          variant: 'error',
          timeout: 5000,
        });
      }
    },
    [
      addNotification,
      update,
      errors.main,
      errors.not_allowed,
      isConnected,
      title,
    ]
  );

  const toggleSocialConnection = useCallback(() => {
    isConnected
      ? disableSocialProvider(connectionCallback, title.toLowerCase())
      : enableSocialProvider(connectionCallback, title.toLowerCase());
  }, [connectionCallback, title, isConnected]);

  if (!isConnected) {
    if (IS_CHINA) {
      return <Typography variant="subtitle2">Not available</Typography>;
    } else {
      return (
        <Button
          variant="outlined"
          size="small"
          onClick={toggleSocialConnection}
        >
          Connect
        </Button>
      );
    }
  }
  return (
    <Button
      color="destructive"
      variant="outlined"
      size="small"
      onClick={toggleSocialConnection}
    >
      Disconnect
    </Button>
  );
};

export default memo(SocialAccountButton);
